import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import range from 'lodash/range';
import round from 'lodash/round';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import types from '../../../../../types';
import BlankLink from '../../../../common/BlankLink';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';

const percentageOptions = t => range(0.20, 2.20, 0.2).concat([2.5, 3]).map(value => ({
  value,
  display: t('{{amount}}%', { amount: round(value * 100) }),
}));

const EfficientCoolingEquipment = ({
  t,
  locale,
  isMetric,
  widgetSource,
  inputFootprintHousingSquarefeet,
  resultTakeactionPurchaseHighEfficiencyCoolingDollarstypical,
  resultTakeactionPurchaseHighEfficiencyCoolingKwhtypical,
  resultTakeactionPurchaseHighEfficiencyCoolingDollarsuse,
  resultTakeactionPurchaseHighEfficiencyCoolingKwhuse,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        <span
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line no-template-curly-in-string
            __html: t('Typical {{areaValue}} {{areaUnit}}<sup>2</sup> homes in my climate spend ${{dollarsTypical}} ({{kwhTypical}} kWh) per year on air conditioning. I use the AC about', {
              areaValue: inputFootprintHousingSquarefeet.ensuredDisplay,
              areaUnit: isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' }),
              dollarsTypical: resultTakeactionPurchaseHighEfficiencyCoolingDollarstypical,
              kwhTypical: resultTakeactionPurchaseHighEfficiencyCoolingKwhtypical,
            }),
          }}
        />
        <InlineSelect
          options={percentageOptions(t)}
          apiKey={'input_takeaction_purchase_high_efficiency_cooling_percentuse'}
        />
        {t('of typical households, or {{resultTakeactionPurchaseHighEfficiencyCoolingDollarsuse}} ({{resultTakeactionPurchaseHighEfficiencyCoolingKwhuse}} kWh) per year.', {
          resultTakeactionPurchaseHighEfficiencyCoolingDollarsuse,
          resultTakeactionPurchaseHighEfficiencyCoolingKwhuse,
        })}
      </p>
      <p>
        {t('I will replace my conventional air conditioner(s) that have a SEER rating of')}
        <InlineInput
          maxLength={3}
          apiKey={'input_takeaction_purchase_high_efficiency_cooling_conventional_rating'}
        />
        {t('with Energy Star air conditioner(s) that have a SEER rating of')}
        <InlineInput
          maxLength={3}
          apiKey={'input_takeaction_purchase_high_efficiency_cooling_energystar_rating'}
        />.
      </p>
      <p>
        {t('Learn more about')}
        &nbsp;
        <a
          rel="noopener noreferrer"
          href="http://energy.gov/energysaver/articles/central-air-conditioning"
          target="_blank"
        >{t('SEER Ratings')}</a>{locale === 'fr' ? ' (en anglais seulement).' : '.'}
      </p>
    </div>
    <div className="action-details__links">
      {widgetSource !== 'ontario' && <a
        href="http://www.coolcalifornia.org/funding-wizard#search/cooling//false/none/false/1/12"
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>}
      {widgetSource === 'ontario' &&
        <div>
          <p>
            {t('Stay comfy for less. Upgrade to a more energy-efficient cooling system with up to $600 to purchase and install new ENERGY STAR® certified central air conditioning systems. To learn more about this program, check out')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text="GreenON.ca" />.
          </p>
          <p>
            {t('If you have a natural gas, oil, propane, electric or wood-stove heated home, you can participate in a discounted home energy audit by an approved energy advisor to make improvements to your home’s energy efficiency which includes replacing air conditioners. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
          <p>
            {t('ENERGY STAR® ceiling fans only require 10% of the electricity needed to run compared to a window air conditioner, they use 50% less energy than standard ceiling fans, and they also move up to 25% more air.')}
          </p>
          <p>
            {t('Use a Save on Energy coupon to save $10 off the purchase of an ENERGY STAR® certified ceiling fan at a participating retailer.')}
          </p>
          <p>
            {t('To learn more about this program, check out GreenON.ca')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text="GreenON.ca" />.
          </p>
        </div>
      }
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Cost of electricity')}
          <InlineInput
            apiKey="input_takeaction_assumption_kwhprice"
            maxLength={3}
          />
          {t('cents per kWh')}
        </li>
        <li>
          {t('Cooling degree-days:')}
          <InlineInput
            maxLength={4}
            apiKey="input_takeaction_purchase_high_efficiency_cooling_cdd"
          />
        </li>
        <li>
          <span
            dangerouslySetInnerHTML={{
              __html: t('The cost difference between a conventional air conditioner and an Energy Star air conditioner is:'),
            }}
          />
          <InlineInput
            currency
            maxLength={7}
            apiKey="input_takeaction_purchase_high_efficiency_cooling_cost_difference"
          />
        </li>
      </ul>
    </div>
  </div>
);

EfficientCoolingEquipment.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  inputFootprintHousingSquarefeet: types.UserInput.isRequired,
  resultTakeactionPurchaseHighEfficiencyCoolingDollarstypical: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyCoolingKwhtypical: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyCoolingDollarsuse: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyCoolingKwhuse: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionPurchaseHighEfficiencyCoolingDollarstypical',
  'resultTakeactionPurchaseHighEfficiencyCoolingKwhtypical',
  'resultTakeactionPurchaseHighEfficiencyCoolingDollarsuse',
  'resultTakeactionPurchaseHighEfficiencyCoolingKwhuse',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
  inputFootprintHousingSquarefeet: s.inputFootprintHousingSquarefeet(state, ownProps),
  locale: s.locale(state),
});

const mapDispatchToProps = () => ({});

export default wrap(
  withT('purchaseHighEfficiencyCooling'),
  connect(mapStateToProps, mapDispatchToProps)(EfficientCoolingEquipment),
);
